import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ConsultingConfig, TeamMemberDto, TeamMemberInput, TeamRateChangeInput } from '@jordan/types';
import {
  Alert,
  Avatar,
  Breadcrumb,
  Button,
  ClientCard,
  DataTable,
  type DataTableColumn,
  ImageUpload,
  Input,
  Modal,
  PageHeader,
  SelectMenu,
  Spinner,
} from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, assetUrl } from '../../lib/api';
import { billingTimeSidebar } from './billingTimeSidebar';

const money = (n: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n);
  } catch {
    return `${n} ${currency}`;
  }
};
const rateLabel = (r: TeamMemberDto['rate']) => (r ? `${money(r.amount, r.currency)}/h` : '—');

interface MemberForm {
  name: string;
  jobTitle: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  workHours: string;
  currency: string;
}
const EMPTY: MemberForm = {
  name: '', jobTitle: '', company: '', email: '', phone: '', website: '',
  addressLine1: '', addressLine2: '', city: '', region: '', postalCode: '', country: '', workHours: '', currency: 'USD',
};
const toForm = (m: TeamMemberDto): MemberForm => ({
  name: m.name,
  jobTitle: m.jobTitle ?? '',
  company: m.company ?? '',
  email: m.email ?? '',
  phone: m.phone ?? '',
  website: m.website ?? '',
  addressLine1: m.addressLine1 ?? '',
  addressLine2: m.addressLine2 ?? '',
  city: m.city ?? '',
  region: m.region ?? '',
  postalCode: m.postalCode ?? '',
  country: m.country ?? '',
  workHours: m.workHours == null ? '' : String(m.workHours),
  currency: m.currency || 'USD',
});

export function TeamPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [members, setMembers] = useState<TeamMemberDto[] | null>(null);
  const [maxHours, setMaxHours] = useState(168);
  const [view, setView] = useState<'card' | 'table'>('table');
  const [err, setErr] = useState<string | null>(null);

  // Create/edit member modal.
  const [editing, setEditing] = useState<TeamMemberDto | 'new' | null>(null);
  const [form, setForm] = useState<MemberForm>(EMPTY);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  // Rate/cost modal + inline work-hours edit.
  const [rateModal, setRateModal] = useState<{ member: TeamMemberDto; kind: 'RATE' | 'COST' } | null>(null);
  const [editingHours, setEditingHours] = useState<{ id: string; value: string } | null>(null);

  const load = () =>
    apiGet<TeamMemberDto[]>('/api/consulting/team')
      .then(setMembers)
      .catch(() => setErr('Could not load team members.'));
  useEffect(() => {
    void load();
    apiGet<ConsultingConfig>('/api/config/consulting').then((c) => setMaxHours(c.maxWorkHours)).catch(() => undefined);
  }, []);

  const openNew = () => {
    setEditing('new');
    setForm(EMPTY);
    setPhotoPreview(null);
    setPhotoDataUrl(undefined);
  };
  const openEdit = (m: TeamMemberDto) => {
    setEditing(m);
    setForm(toForm(m));
    setPhotoPreview(assetUrl(m.photoUrl) ?? null);
    setPhotoDataUrl(undefined);
  };
  const close = () => setEditing(null);
  const set = (key: keyof MemberForm, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.name.trim()) {
      setErr('A team member name is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    const payload: TeamMemberInput = {
      name: form.name.trim(),
      jobTitle: form.jobTitle,
      company: form.company,
      email: form.email,
      phone: form.phone,
      website: form.website,
      addressLine1: form.addressLine1,
      addressLine2: form.addressLine2,
      city: form.city,
      region: form.region,
      postalCode: form.postalCode,
      country: form.country,
      workHours: form.workHours.trim() === '' ? null : Number(form.workHours),
      currency: form.currency.trim() || 'USD',
      ...(photoDataUrl === undefined ? {} : { photoDataUrl }),
    };
    try {
      if (editing === 'new') await apiPost('/api/consulting/team', payload);
      else if (editing) await apiPut(`/api/consulting/team/${editing.id}`, payload);
      close();
      await load();
    } catch {
      setErr('Could not save the team member.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m: TeamMemberDto) => {
    if (!window.confirm(`Delete ${m.name}? This can’t be undone.`)) return;
    try {
      await apiDelete(`/api/consulting/team/${m.id}`);
      await load();
    } catch {
      setErr('Could not delete the team member.');
    }
  };

  const saveHours = async (m: TeamMemberDto, raw: string) => {
    setEditingHours(null);
    const trimmed = raw.trim();
    const value = trimmed === '' ? null : Number(trimmed);
    if (value === m.workHours) return; // unchanged
    if (value != null && (!Number.isFinite(value) || value < 0 || value > maxHours || !Number.isInteger(value))) {
      setErr(`Work hours must be a whole number between 0 and ${maxHours}.`);
      return;
    }
    try {
      const updated = await apiPatch<TeamMemberDto>(`/api/consulting/team/${m.id}/work-hours`, { workHours: value });
      setMembers((list) => (list ? list.map((x) => (x.id === m.id ? updated : x)) : list));
    } catch {
      setErr('Could not save work hours.');
    }
  };

  const columns: DataTableColumn<TeamMemberDto>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      sortValue: (m) => m.name.toLowerCase(),
      render: (m) => (
        <div className="d-flex align-items-center">
          <Avatar size={32} initials={m.name.slice(0, 2)} src={assetUrl(m.photoUrl)} />
          <div className="ms-2">
            <div className="fw-semibold">{m.name}</div>
            {m.jobTitle && <div className="text-muted fs-sm">{m.jobTitle}</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'rate',
      header: 'Rate',
      sortValue: (m) => m.rate?.amount ?? -1,
      render: (m) => (
        <button className="btn btn-link p-0 text-reset text-decoration-none" onClick={() => setRateModal({ member: m, kind: 'RATE' })}>
          {m.rate ? rateLabel(m.rate) : <span className="text-primary">Set rate</span>}
        </button>
      ),
    },
    {
      key: 'cost',
      header: 'Cost',
      sortValue: (m) => m.cost?.amount ?? -1,
      render: (m) => (
        <button className="btn btn-link p-0 text-reset text-decoration-none" onClick={() => setRateModal({ member: m, kind: 'COST' })}>
          {m.cost ? rateLabel(m.cost) : <span className="text-primary">Set cost</span>}
        </button>
      ),
    },
    {
      key: 'workHours',
      header: 'Work hours',
      render: (m) =>
        editingHours?.id === m.id ? (
          <input
            type="number"
            min={0}
            max={maxHours}
            step={1}
            autoFocus
            className="form-control form-control-sm"
            style={{ width: 90 }}
            value={editingHours.value}
            onChange={(e) => setEditingHours({ id: m.id, value: e.target.value })}
            onBlur={() => void saveHours(m, editingHours.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') setEditingHours(null);
            }}
          />
        ) : (
          <button
            className="btn btn-link p-0 text-reset text-decoration-none"
            title="Click to edit"
            onClick={() => setEditingHours({ id: m.id, value: m.workHours == null ? '' : String(m.workHours) })}
          >
            {m.workHours == null ? <span className="text-muted">Variable</span> : `${m.workHours} h weekly`}
          </button>
        ),
    },
    {
      key: 'projects',
      header: 'Projects',
      render: (m) =>
        m.projects.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          <div className="d-flex flex-wrap gap-1">
            {m.projects.map((p) => (
              <span key={p.id} className="badge bg-light text-body border">
                <span className="d-inline-block rounded-circle me-1" style={{ width: 8, height: 8, background: p.color }} />
                {p.name}
              </span>
            ))}
          </div>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (m) => (
        <div className="d-flex justify-content-end gap-1">
          <button className="btn btn-light btn-icon btn-sm rounded-pill border-transparent" title="Edit" onClick={() => openEdit(m)}>
            <i className="ph-pencil" />
          </button>
          <button className="btn btn-light btn-icon btn-sm rounded-pill border-transparent text-danger" title="Delete" onClick={() => void remove(m)}>
            <i className="ph-trash" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <AppChrome secondarySidebar={billingTimeSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Billing &amp; Time - <span className="fw-normal">Team</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Consulting' },
              { label: 'Billing & Time' },
              { label: 'Team' },
            ]}
          />
        }
        actions={
          <Button variant="primary" icon="ph-plus" onClick={openNew}>
            Add member
          </Button>
        }
      />

      <div className="content">
        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        <div className="d-flex align-items-center mb-3">
          <span className="text-muted">{members ? `${members.length} member${members.length === 1 ? '' : 's'}` : ''}</span>
          <div className="btn-group ms-auto">
            <button className={`btn btn-sm ${view === 'card' ? 'btn-primary' : 'btn-light'}`} onClick={() => setView('card')}>
              <i className="ph-squares-four me-1" /> Cards
            </button>
            <button className={`btn btn-sm ${view === 'table' ? 'btn-primary' : 'btn-light'}`} onClick={() => setView('table')}>
              <i className="ph-table me-1" /> Table
            </button>
          </div>
        </div>

        {members === null ? (
          <Spinner />
        ) : members.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="ph-users-three d-block fs-1 text-muted mb-2" />
              <h5 className="mb-1">No team members yet</h5>
              <p className="text-muted mb-3">Add the people who perform work to set their rates and costs.</p>
              <Button variant="primary" icon="ph-plus" onClick={openNew}>
                Add member
              </Button>
            </div>
          </div>
        ) : view === 'card' ? (
          <div className="row g-3">
            {members.map((m) => (
              <div className="col-xl-3 col-md-4 col-sm-6" key={m.id}>
                <ClientCard
                  name={m.name}
                  company={m.jobTitle}
                  imageSrc={assetUrl(m.photoUrl)}
                  rate={rateLabel(m.rate)}
                  email={m.email}
                  phone={m.phone}
                  website={m.website}
                  onEdit={() => openEdit(m)}
                  onDelete={() => void remove(m)}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="card border-0 shadow-sm">
            <DataTable
              columns={columns}
              rows={members}
              getRowKey={(m) => m.id}
              searchable
              searchPlaceholder="Search team…"
              initialSort={{ key: 'name', dir: 'asc' }}
              pageSize={20}
            />
          </div>
        )}
      </div>

      {/* Create / edit member */}
      {editing && (
        <Modal
          open
          size="lg"
          onClose={close}
          title={editing === 'new' ? 'Add team member' : `Edit ${(editing as TeamMemberDto).name}`}
          footer={
            <>
              <Button variant="light" onClick={close}>
                Cancel
              </Button>
              <Button variant="primary" disabled={busy} onClick={() => void save()}>
                {editing === 'new' ? 'Create member' : 'Save changes'}
              </Button>
            </>
          }
        >
          <div className="mb-3">
            <ImageUpload
              previewSrc={photoPreview}
              previewSize={96}
              hint="Square image works best for the card."
              onSelect={(_f, dataUrl) => {
                setPhotoDataUrl(dataUrl);
                setPhotoPreview(dataUrl);
              }}
              onRemove={() => {
                setPhotoDataUrl(null);
                setPhotoPreview(null);
              }}
            />
          </div>

          <h6 className="text-muted text-uppercase fs-sm">Details</h6>
          <div className="row">
            <div className="col-md-6"><Input label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div className="col-md-6"><Input label="Job title" value={form.jobTitle} onChange={(e) => set('jobTitle', e.target.value)} /></div>
            <div className="col-md-6"><Input label="Company" value={form.company} onChange={(e) => set('company', e.target.value)} /></div>
            <div className="col-md-6">
              <Input
                label="Work hours / week"
                type="number"
                min={0}
                max={maxHours}
                step={1}
                value={form.workHours}
                onChange={(e) => set('workHours', e.target.value)}
                hint={`Blank = variable (bills for hours worked). Max ${maxHours}.`}
              />
            </div>
            <div className="col-md-6"><Input label="Email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
            <div className="col-md-6"><Input label="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></div>
            <div className="col-12"><Input label="Website" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://" /></div>
          </div>

          <h6 className="text-muted text-uppercase fs-sm mt-2">Address</h6>
          <div className="row">
            <div className="col-12"><Input label="Address line 1" value={form.addressLine1} onChange={(e) => set('addressLine1', e.target.value)} /></div>
            <div className="col-12"><Input label="Address line 2" value={form.addressLine2} onChange={(e) => set('addressLine2', e.target.value)} /></div>
            <div className="col-md-5"><Input label="City" value={form.city} onChange={(e) => set('city', e.target.value)} /></div>
            <div className="col-md-3"><Input label="State / region" value={form.region} onChange={(e) => set('region', e.target.value)} /></div>
            <div className="col-md-4"><Input label="Postal code" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} /></div>
            <div className="col-12"><Input label="Country" value={form.country} onChange={(e) => set('country', e.target.value)} /></div>
          </div>
          <div className="text-muted fs-sm">Set the charge-out rate and labour cost from the Team table after saving.</div>
        </Modal>
      )}

      {/* Rate / cost change */}
      {rateModal && (
        <RateModal
          member={rateModal.member}
          kind={rateModal.kind}
          onClose={() => setRateModal(null)}
          onSaved={(updated) => {
            setMembers((list) => (list ? list.map((x) => (x.id === updated.id ? updated : x)) : list));
            setRateModal(null);
          }}
          onError={() => setErr('Could not change the rate.')}
        />
      )}
    </AppChrome>
  );
}

/** The "Change billable rate / labour cost" modal (effective for all data or from today). */
function RateModal({
  member,
  kind,
  onClose,
  onSaved,
  onError,
}: {
  member: TeamMemberDto;
  kind: 'RATE' | 'COST';
  onClose: () => void;
  onSaved: (m: TeamMemberDto) => void;
  onError: () => void;
}) {
  const isRate = kind === 'RATE';
  const current = isRate ? member.rate : member.cost;
  const noun = isRate ? 'rate' : 'cost';
  // Rate defaults to all-data, cost to from-today (per the reference dialogs).
  const [effective, setEffective] = useState<'ALL' | 'TODAY'>(isRate ? 'ALL' : 'TODAY');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (amount.trim() === '' || !Number.isFinite(Number(amount))) return;
    setBusy(true);
    try {
      const updated = await apiPost<TeamMemberDto>(`/api/consulting/team/${member.id}/rate`, {
        kind,
        amount: Number(amount),
        effective,
      } satisfies TeamRateChangeInput);
      onSaved(updated);
    } catch {
      onError();
    } finally {
      setBusy(false);
    }
  };

  const btnLabel = isRate
    ? effective === 'ALL'
      ? 'Change rate for all data'
      : 'Change rate starting today'
    : effective === 'ALL'
      ? 'Change cost for all data'
      : 'Change cost starting today';

  return (
    <Modal
      open
      centered
      onClose={onClose}
      title={isRate ? 'Change billable rate?' : 'Change labour cost?'}
      footer={
        <>
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {btnLabel}
          </Button>
        </>
      }
    >
      <p className="text-muted">
        Changing the {noun} updates all time entries and reports associated with it, except where a more granular{' '}
        {noun} has been set or there are time entries spanning midnight.
      </p>
      <div className="row">
        <div className="col-6">
          <Input label={`Current ${noun}`} disabled value={current ? String(current.amount) : '0'} />
        </div>
        <div className="col-6">
          <Input
            label={`New ${noun}`}
            type="number"
            min={0}
            step="0.01"
            autoFocus
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
      </div>
      <div style={{ maxWidth: 280 }}>
        <SelectMenu
          label={`New ${noun} will be effective`}
          icon="ph-info"
          value={effective}
          onChange={(v) => setEffective(v as 'ALL' | 'TODAY')}
          options={[
            { value: 'ALL', label: 'for all data' },
            { value: 'TODAY', label: 'from today' },
          ]}
        />
      </div>
    </Modal>
  );
}
